import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { AxiosRequestConfig } from 'axios';
import {
  getCountry,
  GetCountryRequestType,
  getPurpose,
  GetPurposeRequestType,
  getChannel,
  GetChannelRequestType,
  getPartnerL2,
  getServicePartners,
  GetServicePartnersRequestType,
  GetExtraFieldsRequestType,
  getExtraFields,
} from 'service/apis/CreateDealapi';
import { getBranchList } from 'service/apis/BranchRegistration';
import { selectOptionType } from 'components/SelectBox';

export const useDealData = () => {
  const countries = [
    "United States", "Canada", "Australia", "United Kingdom", 
    "France", "New Zealand", "Singapore"
  ];

  const [servicePartners, setServicePartners] = useState<selectOptionType[]>([]);

  // Helper function to get auth token
  const getAuthToken = () => {
    const auth = localStorage.getItem("authToken");
    return auth ? JSON.parse(auth) : null;
  };

  // Query for fetching branches
  const {
    data: branchData,
    isLoading: isBranchLoading,
    error: branchError,
    refetch: refetchBranches,
  } = useQuery(
    ['branches'],
    async () => {
      // Note: payload seems to be missing in original code, using empty config
      const payload: AxiosRequestConfig = {};
      const res = await getBranchList(payload);
      return res?.data?.map((item: any) => ({
        label: item.name as string,
        value: item.id,
      })) || [];
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onError: (error) => {
        console.error('Error fetching branches:', error);
      },
    }
  );

  // Query for fetching purposes
  const {
    data: purposeData,
    isLoading: isPurposeLoading,
    error: purposeError,
    refetch: refetchPurposes,
  } = useQuery(
    ['purposes'],
    async () => {
      const payload: AxiosRequestConfig<GetPurposeRequestType> = {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: { product_id: 1 },
      };
      
      const res = await getPurpose(payload);
      const filteredPurpose = res?.data?.filter((item: any) => item.id !== 13);
      
      return {
        allPurpose: filteredPurpose,
        purpose: filteredPurpose?.map((item: any) => ({
          label: item.name as string,
          value: String(item.id),
        })) || [],
      };
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: (error) => {
        console.error('Error fetching purposes:', error);
      },
    }
  );

  // Query for fetching countries
  const {
    data: countryData,
    isLoading: isCountryLoading,
    error: countryError,
    refetch: refetchCountries,
  } = useQuery(
    ['countries'],
    async () => {
      const payload: AxiosRequestConfig<GetCountryRequestType> = {};
      const { data } = await getCountry(payload);

      if (!data || data.length === 0) return { countryOption: [], currencyData: [] };

      const countryOrder = countries.reduce<Record<string, number>>(
        (acc, country, index) => {
          acc[country] = index + 1;
          return acc;
        },
        {}
      );

      const sortedData = data.sort(
        (a: any, b: any) =>
          (countryOrder[a.country] || Infinity) -
          (countryOrder[b.country] || Infinity)
      );

      return {
        currencyData: data,
        countryOption: sortedData.map((item: any) => ({
          label: item.country,
          value: item.country,
        })),
      };
    },
    {
      staleTime: 10 * 60 * 1000, // 10 minutes (countries don't change often)
      cacheTime: 30 * 60 * 1000, // 30 minutes
      onError: (error) => {
        console.error('Error fetching country data:', error);
      },
    }
  );

  // Query for fetching partners
  const {
    data: partnerData,
    isLoading: isPartnerLoading,
    error: partnerError,
    refetch: refetchPartners,
  } = useQuery(
    ['partners'],
    async () => {
      const authToken = getAuthToken();
      if (!authToken) throw new Error('No auth token available');
      
      const payload: AxiosRequestConfig<GetChannelRequestType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { per_page: 1000 },
      };

      const res = await getChannel(payload) as any;
      if (res.data) {
        return res?.data?.map((item: any) => ({
          label: item.name,
          value: item.id,
          status: item.status,
          city: item.city ?? '',
        })) || [];
      }
      return [];
    },
    {
      enabled: !!getAuthToken(), // Only run if auth token exists
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: (error) => {
        console.error('Error fetching partners:', error);
      },
    }
  );

  // Query for fetching L2 partner
  const {
    data: l2Partner,
    isLoading: isL2PartnerLoading,
    error: l2PartnerError,
    refetch: refetchL2Partner,
  } = useQuery(
    ['l2Partner'],
    async () => {
      const authToken = getAuthToken();
      if (!authToken) throw new Error('No auth token available');
      
      const payload: AxiosRequestConfig<GetChannelRequestType> = {
        headers: { Authorization: `Bearer ${authToken}` },
      };

      const res = await getPartnerL2(payload);
      return res.data?.ID;
    },
    {
      enabled: !!getAuthToken(),
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      onError: (error) => {
        console.error('Error fetching L2 partner:', error);
      },
    }
  );

  // Function to fetch service partners (still dynamic based on params)
  const fetchServicePartners = async (purposeId: string, currency: string) => {
    if (!purposeId || !currency) {
      return { success: false, partners: [] };
    }

    try {
      const authToken = getAuthToken();
      if (!authToken) return { success: false, partners: [] };
      
      const payload: AxiosRequestConfig<GetServicePartnersRequestType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        params: {
          purpose_id: parseInt(purposeId),
          currency,
        },
      };

      const res = await getServicePartners(payload);
      const partners = res.data.map((item: any) => ({
        label: item.display_name,
        value: item.id,
      }));
      setServicePartners(partners);
      return { success: true, partners: res.data, servicePartners: partners };
    } catch (error) {
      console.error('Error fetching service partners:', error);
      return { success: false, partners: [], error };
    }
  };

  // Function to fetch fields (still dynamic based on params)
  const fetchFields = async (purpose_id_copy: string, service_partner_id: string) => {
    const payload: AxiosRequestConfig<GetExtraFieldsRequestType> = {
      params: {
        purpose_id: purpose_id_copy,
        service_partner_id: service_partner_id as string,
      },
    };

    try {
      const res = await getExtraFields(payload);
      if (res.status === 200) {
        return res.data;
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  // Computed values using useMemo
  const getLevelPurposeOptions = useMemo(() => {
    return purposeData?.purpose?.filter((item: any) => ["1", "2", "3", "4"].includes(item.value)) || [];
  }, [purposeData?.purpose]);

  const updateCurrencyForCountry = (selectedCountry: string) => {
    const countryInfo = countryData?.currencyData?.find((item: any) => item.country === selectedCountry);
    return countryInfo?.currency || '';
  };

  return {
    // Data
    purpose: getLevelPurposeOptions,
    countryOption: countryData?.countryOption || [],
    currencyData: countryData?.currencyData || [],
    servicePartners, // This will be populated by fetchServicePartners
    partnerOption: partnerData || [],
    l2Partner,
    branchOption: branchData || [],
    allPurpose: purposeData?.allPurpose || [],

    // Loading states
    isLoading: {
      branches: isBranchLoading,
      purposes: isPurposeLoading,
      countries: isCountryLoading,
      partners: isPartnerLoading,
      l2Partner: isL2PartnerLoading,
    },

    // Error states
    errors: {
      branches: branchError,
      purposes: purposeError,
      countries: countryError,
      partners: partnerError,
      l2Partner: l2PartnerError,
    },

    // Functions
    fetchServicePartners,
    updateCurrencyForCountry,
    fetchFields,

    // Refetch functions
    refetch: {
      fetchPurpose: refetchPurposes,
      fetchCountry: refetchCountries,
      fetchPartner: refetchPartners,
      fetchPartnerL2: refetchL2Partner,
      fetchBranches: refetchBranches,
    },
  };
};